/**
 * Dialog to insert an image - same UX as AddDocument (drag & drop)
 */

import { useState, useRef } from "react";
import { Modal } from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import CloudUpload from "assets/img/svg/cloudUpload";
import { useTranslation } from "react-i18next";
import UploadSuccess from "assets/img/svg/uploadSuccess";
import { getFileSize } from "helpers/utils";
import StyledButton from "components/shared/StyledButton";
import "components/shared/AddDocument/style.scss";
import type { InsertImagePayload } from "../plugins/ImagesPlugin";

interface InsertImageDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (payload: InsertImagePayload) => void;
}

const IMAGE_MAX_SIZE_MB = 5;
const IMAGE_MAX_SIZE_BYTES = IMAGE_MAX_SIZE_MB * 1024 * 1024;
const DEFAULT_IMAGE_WIDTH = 500;

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPT_ATTRIBUTE = ".jpg,.jpeg,.png,.webp";

function isAllowedImageType(type: string): boolean {
    return ALLOWED_IMAGE_TYPES.includes(type);
}

export default function InsertImageDialog({ open, onClose, onConfirm }: InsertImageDialogProps) {
    const [t] = useTranslation();
    const [file, setFile] = useState<File>();
    const [fileError, setFileError] = useState<string>("");
    const [originalFileName, setOriginalFileName] = useState("");
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const hasImage = !!file;
    const isDisabled = !hasImage;

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileError("");
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) {
            e.target.value = "";
            return;
        }
        if (!isAllowedImageType(selectedFile.type)) {
            setFileError(t("ContractSettings.ImageInvalidType"));
            e.target.value = "";
            return;
        }
        if (selectedFile.size > IMAGE_MAX_SIZE_BYTES) {
            setFileError(t("ContractSettings.ImageFileTooLarge", { max: IMAGE_MAX_SIZE_MB }));
            e.target.value = "";
            return;
        }
        setFile(selectedFile);
        setOriginalFileName(selectedFile.name?.split(".")[0] || "");
        e.target.value = "";
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingOver(false);
        setFileError("");
        const droppedFile = e.dataTransfer.files?.[0];
        if (!droppedFile) return;
        if (!isAllowedImageType(droppedFile.type)) {
            setFileError(t("ContractSettings.ImageInvalidType"));
            return;
        }
        if (droppedFile.size > IMAGE_MAX_SIZE_BYTES) {
            setFileError(t("ContractSettings.ImageFileTooLarge", { max: IMAGE_MAX_SIZE_MB }));
            return;
        }
        setFile(droppedFile);
        setOriginalFileName(droppedFile.name?.split(".")[0] || "");
    };

    const handleValidate = () => {
        if (!file) return;

        const altText = originalFileName || "";

        handleClose();

        const doInsert = (payload: { src: string; altText: string; width?: number; height?: "inherit" }) => {
            setTimeout(
                () =>
                    onConfirm({
                        ...payload,
                        width: payload.width ?? DEFAULT_IMAGE_WIDTH,
                        height: payload.height ?? "inherit",
                    }),
                0,
            );
        };

        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") {
                doInsert({ src: reader.result, altText });
            }
        };
        reader.readAsDataURL(file);
    };

    const handleClose = () => {
        setFile(undefined);
        setOriginalFileName("");
        setFileError("");
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <div className="add-document">
                <div className="add-document-modal">
                    <div className="add-document-header">
                        <div className="add-document-header-title">{t("ContractSettings.InsertImage")}</div>
                        <div className="add-document-header-cross" onClick={handleClose}>
                            <GridCloseIcon />
                        </div>
                    </div>
                    <div className="add-document-body">
                        <div className="add-document-dropzone-group">
                            <span className="add-document-label">{t("ContractSettings.Image")}</span>
                            <div
                                className={`add-document-dropzone ${
                                    isDraggingOver ? "add-document-dropzone-dragging" : ""
                                } ${hasImage ? "accept" : ""}`}
                                onDrop={handleDrop}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setIsDraggingOver(true);
                                }}
                                onDragLeave={() => setIsDraggingOver(false)}
                            >
                                <input
                                    ref={fileInputRef}
                                    id="insert-image-file"
                                    className="add-document-input-file"
                                    type="file"
                                    accept={ACCEPT_ATTRIBUTE}
                                    onChange={handleFileInput}
                                />
                                <div className="add-document-dropzone-headline">
                                    <div className="add-document-icon">
                                        {hasImage ? (
                                            <UploadSuccess style={{ height: "32px", width: "32px" }} />
                                        ) : (
                                            <CloudUpload />
                                        )}
                                    </div>
                                    {!hasImage && (
                                        <div className="add-document-text">
                                            {t("ContractSettings.InsertImageDragHere")}
                                        </div>
                                    )}
                                    {!hasImage && <div className="add-document-subtext">{t("AddDocument.Or")}</div>}
                                    <div className="add-document-subtext">
                                        <label
                                            htmlFor="insert-image-file"
                                            className={`add-document-link ${hasImage ? "success" : ""}`}
                                        >
                                            {file ? originalFileName || file.name : t("AddDocument.ChooseFile")}
                                        </label>
                                        {file && (
                                            <span className="add-document-subtext success">
                                                {" "}
                                                {getFileSize(file.size)}
                                            </span>
                                        )}
                                    </div>
                                    {hasImage && (
                                        <span className="add-document-success-text">
                                            {t("ContractSettings.ImageReady")}
                                        </span>
                                    )}
                                </div>
                                {fileError && (
                                    <div
                                        className="add-document-subtext add-document-error"
                                        style={{ color: "#d32f2f", marginTop: 8 }}
                                    >
                                        {fileError}
                                    </div>
                                )}
                                {!hasImage && !fileError && (
                                    <div className="add-document-subtext add-document-info">
                                        {t("ContractSettings.InsertImageFormats")}
                                        <br />
                                        {t("AddDocument.FileSizeAtMost")}{" "}
                                        <span className="add-document-bold">{IMAGE_MAX_SIZE_MB} MB</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <StyledButton buttonType="primary" disabled={isDisabled} onClick={handleValidate}>
                            {t("AddDocument.Validate")}
                        </StyledButton>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
